import { onMounted, onUnmounted } from 'vue'

// Inside the iframe: observe document height and post it to the parent
// so the loader script can resize the iframe element.
//
// The loader filters by event.origin, so we must post with an explicit
// targetOrigin matching the parent's origin. Since loaders embed across
// many partner domains, we use '*' here — the embedded payload is public,
// so leaking it via postMessage is not a confidentiality concern.

export interface PostHeightOptions {
  /** Optional element to observe. Defaults to document.documentElement. */
  target?: HTMLElement
  /** Throttle delay in milliseconds. Defaults to 50. */
  throttle?: number
}

export function usePostHeight(options: PostHeightOptions = {}): void {
  if (typeof window === 'undefined') return

  const throttleMs = options.throttle ?? 50
  let lastHeight = 0
  let timeout: number | null = null
  let observer: ResizeObserver | null = null

  function send(height: number) {
    if (height === lastHeight) return
    lastHeight = height
    window.parent.postMessage({ type: 'greenbow:resize', height }, '*')
  }

  function measureAndSend() {
    const el = options.target ?? document.documentElement
    const height = Math.ceil(el.getBoundingClientRect().height)
    send(height)
  }

  function schedule() {
    if (timeout !== null) return
    timeout = window.setTimeout(() => {
      timeout = null
      measureAndSend()
    }, throttleMs)
  }

  onMounted(() => {
    const el = options.target ?? document.documentElement
    observer = new ResizeObserver(schedule)
    observer.observe(el)
    measureAndSend()
  })

  onUnmounted(() => {
    if (timeout !== null) window.clearTimeout(timeout)
    observer?.disconnect()
    observer = null
  })
}
